import { useEffect, useRef, useState } from 'react'

import * as Sentry from '@sentry/nextjs'

import { NetworkStatus } from '@apollo/client'

import { useMseStreamQuery } from '@/graphql/generated/hooks'
import { MseStreamQuery } from '@/graphql/generated/operations'

import { transformGraphQLHeaders } from '../utils/utils'

const TIMEOUT_IN_MS = 30_000
const SAFETY_MARGIN_MS = 5 * 60 * 1000 // 5 minutes before expiry

export interface MSEStreamStateIProps {
  url: string | null
  headers: Record<string, string> | undefined
  expiryTime: string | null
  isLoading: boolean
  error: string | null
  refetch: () => void
}

/**
 * Hook for fetching MSE stream information with auth headers
 */
export const useMSEStream = (cameraId?: string): MSEStreamStateIProps => {
  const refreshTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const queryTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [url, setUrl] = useState<string | null>(null)
  const [headers, setHeaders] = useState<Record<string, string> | undefined>(
    undefined
  )
  const [expiryTime, setExpiryTime] = useState<string | null>(null)

  const processData = (
    streamData: NonNullable<MseStreamQuery['streamingUrlWrapper']>
  ) => {
    const {
      url: streamUrl,
      expiryTime: expiry,
      headers: headersArray,
    } = streamData

    // Early return for invalid or incomplete data
    if (!streamUrl || !expiry || !headersArray?.length) {
      const err = new Error('MSE video player: Invalid stream data')
      Sentry.captureException(err)
      setUrl(null)
      setHeaders(undefined)
      setExpiryTime(null)
      setError('Invalid stream data')
      return
    }

    setUrl(streamUrl)
    setHeaders(transformGraphQLHeaders(headersArray))
    setExpiryTime(expiry)
    setError(null)

    // Calculate refresh time: 5 minutes before expiry
    const expiryMs = new Date(expiry).getTime()
    const nowMs = Date.now()
    const refreshInMs = Math.max(0, expiryMs - nowMs - SAFETY_MARGIN_MS)

    // Clear any existing refresh timeout
    if (refreshTimeoutRef.current) {
      clearTimeout(refreshTimeoutRef.current)
    }

    // Schedule refresh before expiry
    refreshTimeoutRef.current = setTimeout(() => {
      refetch()
    }, refreshInMs)
  }

  const handleCompleted = (data: MseStreamQuery) => {
    // Clear the query timeout since we received a response
    if (queryTimeoutRef.current) {
      clearTimeout(queryTimeoutRef.current)
      queryTimeoutRef.current = null
    }

    setIsLoading(false)
    if (data.streamingUrlWrapper) {
      processData(data.streamingUrlWrapper)
    } else {
      const err = new Error('MSE video player: Stream not available')
      Sentry.captureException(err)
      setUrl(null)
      setHeaders(undefined)
      setExpiryTime(null)
      setError('Stream not available')
    }
  }

  const handleQueryTimeout = () => {
    const err = new Error(
      'MSE video player: Request timeout - stream query exceeded 30 seconds'
    )
    Sentry.captureException(err)
    setIsLoading(false)
    setUrl(null)
    setHeaders(undefined)
    setExpiryTime(null)
    setError('Request timeout')
    queryTimeoutRef.current = null
  }

  const handleError = (err: any) => {
    // Clear the query timeout since we received an error response
    if (queryTimeoutRef.current) {
      clearTimeout(queryTimeoutRef.current)
      queryTimeoutRef.current = null
    }
    const wrappedErr = new Error(
      `MSE video player: Error fetching stream - ${
        err.message || 'Unknown error'
      }`
    )
    Sentry.captureException(wrappedErr, {
      extra: { originalError: err },
    })
    setIsLoading(false)
    setUrl(null)
    setHeaders(undefined)
    setExpiryTime(null)
    setError('Error fetching stream')
  }

  const { refetch, networkStatus } = useMseStreamQuery({
    variables: { deviceId: cameraId || '' },
    fetchPolicy: 'no-cache',
    skip: !cameraId,
    onCompleted: handleCompleted,
    onError: handleError,
  })

  useEffect(() => {
    return () => {
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current)
      }
      if (queryTimeoutRef.current) {
        clearTimeout(queryTimeoutRef.current)
      }
    }
  }, [])

  useEffect(() => {
    switch (networkStatus) {
      case NetworkStatus.setVariables:
        setIsLoading(true)
        break
      case NetworkStatus.loading:
        if (!queryTimeoutRef.current) {
          queryTimeoutRef.current = setTimeout(
            handleQueryTimeout,
            TIMEOUT_IN_MS
          )
        }
        break
    }
  }, [networkStatus])

  return {
    url,
    headers,
    expiryTime,
    isLoading,
    error,
    refetch,
  }
}
